import React, { useContext, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiMessageSquare } from "react-icons/fi";
import { ApiContext } from "../../../context/MyContext";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getRoom } from "../../../service/comms/roomsService";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { InboxList } from "./InboxList";
import { Thread } from "./Thread";
import { ClientRail } from "./ClientRail";
import { DetailDrawer } from "./DetailDrawer";
import { Drawer } from "../../../common/comms/Drawer";

/**
 * Messages workspace — the Flex Comms coaching-room chat surface.
 *
 * Three-pane layout with two modes toggled from the thread header:
 *  - chat focus (default): inbox | thread (flex) | client rail (compact, 3 tabs)
 *  - client focus: inbox collapses to an avatar strip, rail expands to the main pane
 * Routes: /messages (first room), /messages/:roomId (room open).
 */
export default function MessagesWorkspace() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { empId } = useContext(ApiContext);
  const viewerUserId = empId;
  const { getClientName } = useCommsIdentity();
  const isMobile = useIsMobile();

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [focusMode, setFocusMode] = useState("chat"); // 'chat' | 'client'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false); // mobile-only: client rail drawer
  const [composerPrefill, setComposerPrefill] = useState(null); // { text, nonce }

  // Only one overlay drawer at a time on mobile.
  const handleOpenDetailDrawer = useCallback(() => {
    setRailOpen(false);
    setDrawerOpen(true);
  }, []);
  const handleOpenRail = useCallback(() => {
    setDrawerOpen(false);
    setRailOpen(true);
  }, []);

  const handleBackToInbox = useCallback(() => {
    setSelectedRoom(null);
    navigate("/messages");
  }, [navigate]);

  // Seed the thread composer with a renewal check-in draft and switch to chat
  // focus so the coach lands on the composer ready to send.
  const handleSendCheckIn = useCallback((text) => {
    setComposerPrefill({ text, nonce: Date.now() });
    setFocusMode("chat");
  }, []);

  const handleSelectRoom = useCallback(
    (room) => {
      setSelectedRoom(room);
      if (room?.id) navigate(`/messages/${room.id}`);
    },
    [navigate]
  );

  // Direct nav / refresh on /messages/:roomId — hydrate the room object
  // (needed for the rail's client_id + drawer) if the inbox click didn't set it.
  useEffect(() => {
    let active = true;
    if (roomId && (!selectedRoom || String(selectedRoom.id) !== roomId)) {
      getRoom(commsGet, roomId)
        .then((r) => {
          if (active && r) setSelectedRoom((prev) => (prev && String(prev.id) === roomId ? prev : r));
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const toggleFocus = useCallback(
    () => setFocusMode((m) => (m === "chat" ? "client" : "chat")),
    []
  );

  const isChat = focusMode === "chat";
  const clientId = selectedRoom?.client_id;
  const clientName =
    selectedRoom?.client_name ||
    getClientName(clientId) ||
    (selectedRoom?.display_id ? `Client #${selectedRoom.display_id}` : "Client");

  // 100dvh (not 100vh) so a collapsing mobile URL bar can never push the
  // composer below the fold.
  if (isMobile) {
    return (
      <div className="flex h-[100dvh] min-h-0 overflow-hidden bg-app-bg">
        {roomId ? (
          <div className="flex h-full w-full min-w-0 flex-col bg-white overflow-hidden">
            <Thread
              roomId={roomId}
              viewerUserId={viewerUserId}
              onBack={handleBackToInbox}
              onToggleRail={handleOpenRail}
              onOpenDrawer={handleOpenDetailDrawer}
              composerPrefill={composerPrefill}
            />
          </div>
        ) : (
          <div className="h-full w-full min-w-0 bg-white overflow-hidden">
            <InboxList
              selectedRoomId={roomId}
              onSelectRoom={handleSelectRoom}
              viewerUserId={viewerUserId}
              mode="full"
            />
          </div>
        )}

        <Drawer
          isOpen={railOpen}
          onClose={() => setRailOpen(false)}
          title="Client details"
          width={420}
        >
          {clientId ? (
            <ClientRail
              clientId={clientId}
              roomId={roomId}
              mode="full"
              layout="flow"
              onSendCheckIn={handleSendCheckIn}
            />
          ) : (
            <p className="text-sm text-gray-400 p-4">Loading client details…</p>
          )}
        </Drawer>

        <DetailDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          roomId={roomId}
          room={selectedRoom}
          clientName={clientName}
        />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4.5rem)] gap-3 p-3 bg-app-bg">
      {/* Inbox pane — full list (chat focus) or avatar strip (client focus) */}
      <div
        className={`shrink-0 bg-white rounded-xl shadow-md overflow-hidden ${
          isChat ? "w-[260px]" : "w-14"
        } ${isChat ? "" : "hidden sm:block"}`}
      >
        <InboxList
          selectedRoomId={roomId}
          onSelectRoom={handleSelectRoom}
          viewerUserId={viewerUserId}
          mode={isChat ? "full" : "strip"}
        />
      </div>

      {/* Thread pane — always survives. In chat focus it's deliberately the
          small pane (~28%) on md+; the client rail takes the rest. */}
      <div
        className={`min-w-0 bg-white rounded-xl shadow-md overflow-hidden flex flex-col ${
          isChat
            ? "flex-1 md:flex-none md:w-[28%] md:min-w-[300px] md:shrink-0"
            : "w-[320px] shrink-0"
        }`}
      >
        {roomId ? (
          <Thread
            roomId={roomId}
            viewerUserId={viewerUserId}
            onToggleRail={toggleFocus}
            onOpenDrawer={() => setDrawerOpen(true)}
            composerPrefill={composerPrefill}
          />
        ) : (
          <EmptyThread />
        )}
      </div>

      {/* Client rail — compact (chat focus) or expanded main pane (client focus).
          Deliberately wide (the thread shrinks) and always present while a
          room is open — a placeholder holds the pane until client_id loads. */}
      {roomId && (
        <div
          className={`bg-white rounded-xl shadow-md overflow-hidden ${
            isChat ? "hidden md:block flex-1 min-w-0" : "flex-1 min-w-0"
          }`}
        >
          {clientId ? (
            <ClientRail
              clientId={clientId}
              roomId={roomId}
              mode={isChat ? "compact" : "full"}
              onSendCheckIn={handleSendCheckIn}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-4">
              <p className="text-sm text-gray-400">Loading client details…</p>
            </div>
          )}
        </div>
      )}

      <DetailDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        roomId={roomId}
        room={selectedRoom}
        clientName={clientName}
      />
    </div>
  );
}

function EmptyThread() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
      <FiMessageSquare size={40} />
      <p className="text-sm">Select a conversation to start</p>
    </div>
  );
}
